import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactElement,
  Dispatch,
  SetStateAction,
} from "react";
import { Viewer, Worker, SpecialZoomLevel, LoadError, Plugin } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin, SidebarTab } from "@react-pdf-viewer/default-layout";

import "@react-pdf-viewer/highlight/lib/styles/index.css";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";

// eslint-disable-next-line cohere-react/no-mui-styled-import
import { styled, Theme } from "@material-ui/core";
import DehazeIcon from "@material-ui/icons/Dehaze";
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import PdfViewerRenderError from "./PdfViewerRenderError";
import FaxOutcomeToolbarControl, { FaxOutcomeToolbarControlProps } from "./FaxOutcomeToolbarControl";
import { usePdfInteractionHandlers } from "./hooks/usePdfInteractionHandlers";

interface Props {
  pdfUrl: string;
  hideSidePanelButton?: boolean;
  sidePanelComponent?: (params: FaxOutcomePdfViewerSidePanelProps) => ReactElement;
}

export interface FaxOutcomePdfViewerSidePanelProps {
  isOpenSidePanel?: boolean;
  setIsOpenSidePanel?: Dispatch<SetStateAction<boolean>>;
  sidePanelRef?: (node: React.ReactNode) => void;
  jumpToPage?: (pageIndex: number) => void;
}

export default function FaxOutcomePdfViewer({ pdfUrl, sidePanelComponent, hideSidePanelButton = false }: Props) {
  if (typeof window !== "undefined") {
    window.ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;
  }

  const [slideInSidePanel, setSlideInSidePanel] = useState(!hideSidePanelButton);
  const [sidePanelIsMounted, setSidePanelIsMounted] = useState(!hideSidePanelButton);

  const currentPage = React.useRef<number>(0);
  const currentRotate = React.useRef<number>(0);
  const zoomLevel = React.useRef<number>(1);

  const textInputRef = useRef<HTMLInputElement>();

  // Toolbar rendering
  const renderToolbar = useCallback(
    ({ currentRotate, openSidePanel, setOpenSidePanel, Toolbar }: FaxOutcomeToolbarControlProps) => (
      <FaxOutcomeToolbarControl
        currentRotate={currentRotate}
        openSidePanel={openSidePanel}
        setOpenSidePanel={setOpenSidePanel}
        hideSidePanelButton={hideSidePanelButton}
        Toolbar={Toolbar}
      />
    ),
    [hideSidePanelButton]
  );

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: {
      searchPlugin: {
        onHighlightKeyword: (props) => {
          props.highlightEle.style.pointerEvents = "none";
        },
      },
    },
    renderToolbar: (Toolbar) =>
      renderToolbar({
        Toolbar,
        currentRotate,
        openSidePanel: slideInSidePanel,
        setOpenSidePanel: setSlideInSidePanel,
        loadingRemainingAttachments: false,
        textInputRef,
      }),
    sidebarTabs: () => sideBarTabs,
  });

  const { toolbarPluginInstance } = defaultLayoutPluginInstance;
  const { pageNavigationPluginInstance } = toolbarPluginInstance;
  const { jumpToPage, CurrentPageLabel } = pageNavigationPluginInstance;

  let plugins: Plugin[] = [defaultLayoutPluginInstance];

  // Side panel rendering
  /* these need to stay stable throughout the component lifecycle or we get
    "Maximum update depth exceeded" errors
   */
  const stableJumpToPage = useRef(jumpToPage);
  const stableCurrentPageLabel = useRef(CurrentPageLabel);

  const sidePanelRef = useCallback(
    (node: React.ReactNode) => {
      if (node !== null || slideInSidePanel) {
        setSidePanelIsMounted(true);
      } else {
        setSidePanelIsMounted(false);
      }
    },
    [slideInSidePanel]
  );

  const sidePanel = useMemo(() => {
    if (!!sidePanelComponent) {
      const jumpToPage = stableJumpToPage.current;
      const CurrentPageLabel = stableCurrentPageLabel.current;

      return {
        content: (
          <CurrentPageLabel>
            {() =>
              sidePanelComponent({
                isOpenSidePanel: slideInSidePanel,
                setIsOpenSidePanel: setSlideInSidePanel,
                sidePanelRef,
                jumpToPage,
              })
            }
          </CurrentPageLabel>
        ),
        icon: <DehazeIcon />,
        title: "Attachment list",
      };
    } else {
      return null;
    }
  }, [sidePanelComponent, sidePanelRef, slideInSidePanel]);

  const [sideBarTabs, setSideBarTabs] = useState<SidebarTab[]>(slideInSidePanel && sidePanel ? [sidePanel] : []);

  useEffect(() => {
    /** after slide out animation completes, remove sidePanel component */
    if (!sidePanelIsMounted) {
      setSideBarTabs([]);
    }
  }, [sidePanelIsMounted]);

  const { handleDocumentLoad, handlePageChange, handlePageRotation, handleZoomChange } = usePdfInteractionHandlers({
    defaultLayoutPluginInstance,
    textInputRef,
    currentPage,
    currentRotate,
    zoomLevel,
    sidePanel,
    slideInSidePanel,
    setSideBarTabs,
    setSlideInSidePanel,
    sidePanelIsMounted,
  });

  const renderError = (error: LoadError) => {
    return (
      <PdfViewerRenderError error={error} openSidePanel={slideInSidePanel} setOpenSidePanel={setSlideInSidePanel} />
    );
  };

  return (
    <>
      <DocumentWrapper hideBorders scrollingDisabled={false} hasViewerConfig={slideInSidePanel}>
        <Worker workerUrl="/pdf.worker.min.js">
          <PdfViewerDiv hasViewerConfig={slideInSidePanel}>
            <Viewer
              fileUrl={pdfUrl}
              plugins={plugins}
              onDocumentLoad={handleDocumentLoad}
              defaultScale={SpecialZoomLevel.PageWidth}
              initialPage={currentPage?.current || 0}
              onPageChange={handlePageChange}
              onZoom={handleZoomChange}
              onRotate={handlePageRotation}
              renderError={renderError}
            />
          </PdfViewerDiv>
        </Worker>
      </DocumentWrapper>
    </>
  );
}

// eslint-disable-next-line cohere-react/no-mui-styled-import
const DocumentWrapper = styled(({ hideBorders, scrollingDisabled, hasViewerConfig, ...other }) => <div {...other} />)<
  Theme,
  { hideBorders?: boolean; scrollingDisabled: boolean; hasViewerConfig?: boolean }
>(({ theme, hideBorders, scrollingDisabled, hasViewerConfig }) => ({
  position: "relative",
  width: "100%",
  "& .rpv-default-layout__sidebar-headers": {
    display: "none",
  },
  "& .rpv-default-layout__container": {
    borderStyle: hideBorders ? "none" : "1px solid rgba(0,0,0,.3)",
    border: "1px solid rgba(0,0,0,0.1)",
  },
  "& .rpv-default-layout__sidebar--opened": {
    width: "300px",
    height: "100%",
  },
  "& .rpv-default-layout__sidebar--ltr": {
    borderRight: "1px solid #E5E5E5",
  },
  "& .rpv-core__splitter": {
    display: "none",
  },
  "& .rpv-core__inner-pages": {
    overflow: scrollingDisabled ? "hidden !important" : "auto",
  },
}));

// eslint-disable-next-line cohere-react/no-mui-styled-import
const PdfViewerDiv = styled(({ modalView, crrStyling, hasViewerConfig, ...other }) => <div {...other} />)<
  Theme,
  { modalView?: boolean; crrStyling?: boolean; hasViewerConfig?: boolean }
>(({ modalView, crrStyling, hasViewerConfig }) => ({
  display: "flex",
  width: "100%",
  height: hasViewerConfig ? "100vh" : modalView ? 948 : crrStyling ? "calc(100vh - 148px)" : "100vh",
  "& .rpv-search__highlights": {
    "& .rpv-search__highlight": {
      opacity: "0.3",
      backgroundColor: "#C7C7C7",
    },
    "& .rpv-search__highlight--current": {
      backgroundColor: "#6E6E6E",
    },
  },
}));
